import { BaseControl, IControlParameters } from '@Common/Controls/BaseControl';
import { Control, IAdjustable, isAdjustable, isControl } from '@Common/Controls/Controls';
import { safeDestroys } from '@Common/Core/Destroyable';
import { Scroll } from '@View/Core/Scroll';

export interface IScrollablePanelParameter extends IControlParameters {
    hScroll?: boolean;
    vScroll?: boolean;
    scrollToSelection?: boolean;
}

const defaultSetting: IScrollablePanelParameter = {
    css: null,
    className: '',
    width: '100%',
    height: '100%',
    hScroll: true,
    vScroll: true,
    scrollToSelection: false
};

type ScrollableControl = (Control & Partial<IAdjustable> & { destroy?(): void }) | JQuery | string;

export class ScrollablePanel extends BaseControl {
    private control: ScrollableControl | null = null;
    private readonly scroll: Scroll;
    private readonly content: JQuery;

    constructor(param?: IScrollablePanelParameter) {
        super();
        this._param = _.defaults(param, defaultSetting);
        this._root = $('<div class="scrollable-panel-wrapper"/>');
        this.content = $('<div class="scrollable-panel-scroller"/>').appendTo(this._root);

        this._applyDefaultParam();

        this.scroll = new Scroll(this._root, {
            scroller: this.content,
            scrollX: this._param.hScroll,
            scrollY: this._param.vScroll,
            scrollToSelection: this._param.scrollToSelection
        });
    }

    public adjust(width?: number, height?: number, hPaddings: number = 0): void {
        super.adjust(width, height);

        const hasVertical = this.scroll.hasVerticalScrollbar();
        const hasHorizontal = this.scroll.hasHorizontalScrollbar();

        const newHeight = height ? height - hPaddings : undefined;
        this.adjustControl(newHeight);

        this.scroll.refresh(() => {
            if (hasVertical !== this.scroll.hasVerticalScrollbar() || hasHorizontal !== this.scroll.hasHorizontalScrollbar()) {
                this.adjustControl(newHeight);
            }
        });
    }

    public getScrollContainer(): JQuery<HTMLElement> {
        return this.content;
    }

    public getScrollPlugin(): Scroll {
        return this.scroll;
    }

    public append(control: ScrollableControl, pending?: boolean): void {
        this.control = control;

        if (this.control) {
            this.content.append(this.getAppendableControl(this.control));
        }

        if (!pending) {
            this.scroll.refresh();
        }
    }

    public setContent(object: ScrollableControl): void {
        this.clear();
        this.append(object);
        this.adjust();
    }

    public detach(): void {
        if (this.control) {
            this.getAppendableControl(this.control).detach();
        }
    }

    public clear(): void {
        this.content.empty();
    }

    public refreshScroll(): void {
        this.scroll.refresh();
        this.adjust();
    }

    public ensureVisible(element: any, time?: number, deltaX?: number, deltaY?: number): void {
        this.scroll.ensureVisible(element, deltaX, deltaY, time);
    }

    public destroy(): void {
        super.destroy();
        safeDestroys(this.control, this.scroll, this._root);
    }

    private adjustControl(height?: number): void {
        if (isAdjustable(this.control)) {
            this.control.adjust(this.content.width(), height);
        }
    }

    private getAppendableControl(control: ScrollableControl): JQuery {
        return isControl(control) ? control.getControl() : (control as JQuery);
    }
}
